import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { useLanguage } from "../contexts/LanguageContext";
import CustomerLayout from "../components/CustomerLayout";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle, Wallet, Calendar, CreditCard } from "lucide-react";

const CreditPackages = () => {
  const { language } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPackages = async () => {
      try {
        const response = await api.get("/packages");
        if (isMounted) {
          setPackages(response.data);
          setLoading(false);
        }
      } catch (error) {
        console.error("Packages fetch error:", error);
        if (isMounted) setLoading(false);
      }
    };
    fetchPackages();
    return () => { isMounted = false; };
  }, []);

  const handlePurchase = async (packageId, currency = "usdttrc20") => {
    setPurchasing(packageId);
    try {
      const response = await api.post(
        `/credits/purchase-crypto/${packageId}?origin_url=${encodeURIComponent(window.location.origin)}&pay_currency=${currency}`
      );
      if (response.data.invoice_url) {
        window.location.href = response.data.invoice_url;
      } else {
        throw new Error(language === "tr" ? "Ödeme sayfası oluşturulamadı" : "Could not create payment page");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || (language === "tr" ? "Satın alma başarısız" : "Purchase failed");
      toast.error(errorMessage, {
        duration: 5000,
        description: error.response?.data?.detail
          ? undefined
          : (language === "tr" ? "Lütfen tekrar deneyin veya yöneticiye bildirin." : "Please try again or contact administrator."),
      });
      setPurchasing(null);
    }
  };

  const getTemplateClass = (template, isFeatured) => {
    if (isFeatured || template === "popular") return "package-popular";
    if (template === "premium") return "package-premium";
    if (template === "gold") return "package-gold";
    if (template === "silver") return "package-silver";
    if (template === "platinum") return "package-platinum";
    if (template === "startup") return "package-startup";
    if (template === "agency") return "package-agency";
    if (template === "unlimited") return "package-unlimited";
    return "package-default";
  };

  const subscriptionPackages = packages.filter((p) => p.product_type === "subscription");

  return (
    <CustomerLayout activeId="subscription">
      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div className="text-center mb-10">
          <h1 className="text-2xl md:text-4xl font-mono font-bold tracking-tight uppercase mb-3 flex items-center justify-center gap-3">
            <CreditCard className="w-7 h-7 text-primary flex-shrink-0" />
            {language === "tr" ? "ABONELİK" : "SUBSCRIPTION"}{" "}
            <span className="gradient-text">{language === "tr" ? "PAKETLERİ" : "PACKAGES"}</span>
          </h1>
          <p className="text-muted-foreground font-mono text-sm max-w-2xl mx-auto">
            {language === "tr"
              ? "Sipariş verebilmek için size uygun abonelik paketini seçin. Kripto dahil tüm ödeme yöntemleri desteklenir."
              : "Choose the subscription package that fits you. All payment methods including crypto are supported."}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground font-mono">
            {language === "tr" ? "Yükleniyor..." : "Loading..."}
          </div>
        ) : subscriptionPackages.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground font-mono">
            {language === "tr" ? "Henüz paket yok." : "No packages yet."}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {subscriptionPackages.map((pkg) => (
              <div
                key={pkg.id}
                className={`package-shell h-full p-6 sm:p-8 relative transition-all duration-300 hover:-translate-y-2 flex flex-col ${getTemplateClass(pkg.template, pkg.is_featured)}`}
              >
                {pkg.is_featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-black text-xs font-mono font-bold">
                    {language === "tr" ? "EN POPÜLER" : "MOST POPULAR"}
                  </div>
                )}
                <h3 className="font-mono font-bold text-xl mb-2">
                  {language === "tr" ? (pkg.name_tr || pkg.name) : (pkg.name_en || pkg.name)}
                </h3>
                {(pkg.description || pkg.description_tr || pkg.description_en) && (
                  <p className="text-sm text-muted-foreground mb-4 min-h-[2.5rem]">
                    {language === "tr"
                      ? (pkg.description_tr || pkg.description)
                      : (pkg.description_en || pkg.description)}
                  </p>
                )}
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-4xl font-mono font-bold text-primary">${pkg.price}</span>
                </div>
                <div className="space-y-3 mb-6 flex-1">
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-primary" />
                    <span>{pkg.days} {language === "tr" ? "gün abonelik" : "days subscription"}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle className="w-4 h-4" />
                    <span>
                      {language === "tr"
                        ? "Abonelik süresince sipariş hakkı aktif olur"
                        : "Ordering stays enabled during the subscription"}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={() => handlePurchase(pkg.id)}
                  disabled={purchasing === pkg.id}
                  className="w-full mt-auto font-mono rounded-none bg-primary text-black hover:bg-primary/90"
                  data-testid={`buy-package-${pkg.id}`}
                >
                  {purchasing === pkg.id ? (
                    language === "tr" ? "İşleniyor..." : "Processing..."
                  ) : (
                    <>
                      <Wallet className="w-4 h-4 mr-2" />
                      {language === "tr" ? "KRİPTO İLE SATIN AL" : "BUY WITH CRYPTO"}
                    </>
                  )}
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground font-mono">
            <Sparkles className="w-4 h-4 inline mr-1" />
            {language === "tr"
              ? "Kripto (Bitcoin, Ethereum, USDT vb.) dahil tüm ödeme yöntemleri desteklenir"
              : "All payment methods including crypto (Bitcoin, Ethereum, USDT etc.) are supported"}
          </p>
        </div>
      </div>
    </CustomerLayout>
  );
};

export default CreditPackages;
